import React, { useEffect, useState } from "react";
import { Spinner, Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";

const SelectGenrateInvoice = () => {
  const navigate = useNavigate();

  // Data Lists
  const [categories, setCategories] = useState([]);
  const [grades, setGrades] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);

  // Form State — only the three required selectors
  const [formData, setFormData] = useState({
    category: "",
    grade: "",
    academicYear: "",
    wallet_bal: false,
  });

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [errors, setErrors] = useState({});

  /* ---------------- API CALLS ---------------- */

  const fetchInitialData = async () => {
    const commonBody = JSON.stringify({
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      language: "en",
    });

    try {
      const catRes = await simplePostCall(ApiConfig.categorytype, commonBody);
      if (catRes?.fee_category) setCategories(catRes.fee_category);

      const gradeRes = await simplePostCall(ApiConfig.classlist, commonBody);
      if (gradeRes?.classread) setGrades(gradeRes.classread);

      const yearRes = await simplePostCall(ApiConfig.academicyear, commonBody);
      if (yearRes?.academicYear) setAcademicYears(yearRes.academicYear);
    } catch (err) {
      console.error("Initialization failed", err);
    }
  };

  const fetchStudentsByGrade = async (gradeId) => {
    if (!gradeId) return;
    setIsTableLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.Studentclasslist,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")),
          api_key: localStorage.getItem("api_key"),
          class_id: gradeId,
        }),
      );
      setStudentList(res?.academicYear || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  /* ---------------- HANDLERS ---------------- */

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));

    setErrors((prev) => {
      const next = { ...prev };
      if (val !== "" && next[name]) delete next[name];
      return next;
    });

    if (name === "grade") {
      fetchStudentsByGrade(value);
      setSelectedStudents([]);
      setSelectAll(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    setSelectedStudents(isChecked ? studentList.map((s) => s.student_id) : []);
  };

  /* ---------------- VALIDATION ---------------- */
  const validateForm = () => {
    const tempErrors = {};
    if (!formData.category) tempErrors.category = "Category is required";
    if (!formData.grade) tempErrors.grade = "Grade is required";
    if (!formData.academicYear) tempErrors.academicYear = "Academic Year is required";
    if (selectedStudents.length === 0)
      tempErrors.students = "Select at least one student";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    // Amounts are pulled from the fee structure on the server — no per-student
    // amount/discount/penalty inputs on this screen.
    const payload = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: localStorage.getItem("user_id"),
      category: [Number(formData.category)],
      class_id: formData.grade,
      accademic: formData.academicYear,
      wallet_bal: formData.wallet_bal ? "on" : "off",
      student: selectedStudents,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.Yselectedinvoice,
        JSON.stringify(payload),
      );
      if (res?.result) {
        toast.success("Invoices generated successfully!");
        setTimeout(() => navigate("/UpcomingInvoices"), 1500);
      } else {
        toast.error(res?.message || "Submit failed");
      }
    } catch (err) {
      toast.error("An error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="SelectStudent-container">
      <ToastContainer />
      <div className="special-header panel-white-all">
        <h4 className="page-title">Generate Invoice</h4>
      </div>

      <div className="yc-list-body">
        <div className="card">
          <div className="card-body">
            <Form onSubmit={handleSubmit}>
              <Row className="mb-4">
                <Col md={4} className="mb-3">
                  <Form.Label>
                    Select Category <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    isInvalid={!!errors.category}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.fee_category_id} value={cat.fee_category_id}>
                        {cat.fee_category_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.category}
                  </Form.Control.Feedback>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Label>
                    Select Grade <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="grade"
                    value={formData.grade}
                    onChange={handleInputChange}
                    isInvalid={!!errors.grade}
                  >
                    <option value="">Select Grade</option>
                    {grades.map((g) => (
                      <option key={g.class_id} value={g.class_id}>
                        {g.class_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.grade}
                  </Form.Control.Feedback>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Label>
                    Select Academic Year <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="academicYear"
                    value={formData.academicYear}
                    onChange={handleInputChange}
                    isInvalid={!!errors.academicYear}
                  >
                    <option value="">Select Academic Year</option>
                    {academicYears.map((y) => (
                      <option key={y.academic_year_id} value={y.academic_year_id}>
                        {y.academic_year_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.academicYear}
                  </Form.Control.Feedback>
                </Col>

                <Col md={12}>
                  <Form.Check
                    type="checkbox"
                    name="wallet_bal"
                    label="Collect amount from student wallet"
                    checked={formData.wallet_bal}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>

              {/* Simple checkbox grid of students */}
              <div className="gen-inv-students-section mt-2">
                {isTableLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : studentList.length > 0 ? (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                      <h6 className="mb-0 fw-bold">
                        Select Students
                        <span className="text-muted fw-normal ms-2">
                          ({selectedStudents.length} of {studentList.length} selected)
                        </span>
                      </h6>
                      <Form.Check
                        type="checkbox"
                        id="gen-inv-select-all"
                        label="Select All"
                        onChange={handleSelectAll}
                        checked={
                          studentList.length > 0 &&
                          selectedStudents.length === studentList.length
                        }
                      />
                    </div>

                    <div className="gen-inv-student-grid">
                      {studentList.map((student) => {
                        const isSelected = selectedStudents.includes(student.student_id);
                        return (
                          <label
                            key={student.student_id}
                            className={`gen-inv-student-card ${isSelected ? "is-selected" : ""}`}
                            htmlFor={`gen-inv-stu-${student.student_id}`}
                          >
                            <Form.Check
                              type="checkbox"
                              id={`gen-inv-stu-${student.student_id}`}
                              checked={isSelected}
                              onChange={() => toggleStudentSelection(student.student_id)}
                            />
                            <div className="gen-inv-student-info">
                              <div className="gen-inv-student-name">
                                {student.student_name}
                              </div>
                              <div className="gen-inv-student-meta">
                                {student.student_admission_num || `ID: ${student.student_id}`}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {errors.students && (
                      <div className="text-danger mt-2">{errors.students}</div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-3 text-muted">
                    No students found. Please select a grade.
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  className="btn btn-primary px-5"
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner size="sm" /> : "Submit"}
                </button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectGenrateInvoice;
